import { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Spinner,
} from 'reactstrap';
import { Btn } from '../../../AbstractElements';
import { toast } from 'react-toastify';
import AppVersionService from '../../../Api/services/app-version.service';
import type { components } from '../../../Api/types/openapi';
import { useTranslation } from 'react-i18next';

type AppVersionResponse = components['schemas']['AppVersionResponse'];
type CreateAppVersion = components['schemas']['CreateAppVersion'];
type UpdateAppVersion = components['schemas']['UpdateAppVersion'];

interface AppVersionFormModalProps {
  isOpen: boolean;
  toggle: () => void;
  version: AppVersionResponse | null;
  isEditing: boolean;
  onSuccess: () => void;
}

const AppVersionFormModal: React.FC<AppVersionFormModalProps> = ({
  isOpen,
  toggle,
  version,
  isEditing,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    version: '',
    url: '',
    description: '',
  });

  useEffect(() => {
    if (isOpen) {
      if (version && isEditing) {
        setFormData({
          version: version.version || '',
          url: version.url || '',
          description: version.description || '',
        });
      } else {
        setFormData({
          version: '',
          url: '',
          description: '',
        });
      }
    }
  }, [isOpen, version, isEditing]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.version || !formData.version.trim()) {
      toast.error(t('La versión es requerida'));
      return;
    }

    if (!formData.url || !formData.url.trim()) {
      toast.error(t('La URL es requerida'));
      return;
    }

    // Validar formato de URL
    try {
      new URL(formData.url.trim());
    } catch {
      toast.error(t('La URL no tiene un formato válido'));
      return;
    }

    try {
      setLoading(true);
      
      if (isEditing && version?.id) {
        const updateData: UpdateAppVersion = {
          id: version.id,
          version: formData.version.trim(),
          url: formData.url.trim(),
          description: formData.description.trim() || null,
        };
        await AppVersionService.updateAppVersion(updateData);
        toast.success(t('Versión actualizada exitosamente'));
      } else {
        const createData: CreateAppVersion = {
          version: formData.version.trim(),
          url: formData.url.trim(),
          description: formData.description.trim() || null,
        };
        await AppVersionService.createAppVersion(createData);
        toast.success(t('Versión creada exitosamente'));
      }
      
      toggle();
      onSuccess();
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || t('Error al guardar versión');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        {isEditing ? t('Editar Versión') : t('Crear Nueva Versión')}
      </ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <FormGroup>
            <Label>{t('Versión')} *</Label>
            <Input
              type="text"
              value={formData.version}
              onChange={(e) => setFormData({ ...formData, version: e.target.value })}
              placeholder={t('Ejemplo: 1.0.0')}
              required
            />
            <small className="form-text text-muted">
              {t('Formato recomendado: número.número.número (ej: 1.0.0)')}
            </small>
          </FormGroup>

          <FormGroup>
            <Label>{t('URL')} *</Label>
            <Input
              type="url"
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              placeholder={t('https://ejemplo.com/app.apk')}
              required
            />
            <small className="form-text text-muted">
              {t('URL completa donde se encuentra el archivo APK')}
            </small>
          </FormGroup>

          <FormGroup>
            <Label>{t('Descripción')}</Label>
            <Input
              type="textarea"
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder={t('Descripción de los cambios en esta versión...')}
            />
            <small className="form-text text-muted">
              {t('Opcional: Descripción de los cambios o mejoras en esta versión')}
            </small>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Btn color="secondary" onClick={toggle} disabled={loading}>
            {t('Cancelar')}
          </Btn>
          <Btn color="primary" type="submit" disabled={loading}>
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                {t('Guardando...')}
              </>
            ) : (
              t('Guardar')
            )}
          </Btn>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default AppVersionFormModal;

